import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import { VscTrash, VscCopy, VscCheck } from 'react-icons/vsc';
import { MdPrint } from 'react-icons/md';
import PrintButton from '../common/PrintButton';
import { getLabelPrinter, submitLabelJob } from '../../api/labelJobs';
import { getCableLabelDataUrl } from '../../utils/labelUtils';
import { getCables, createCable, updateCable, deleteCable } from '../../api/cables';
import { getItems } from '../../api/items';
import { getCableTypes } from '../../api/cableTypes';
import { getSections } from '../../api/sections';
import { getBreakers } from '../../api/breakers';
import SearchSelect from '../common/SearchSelect';
import RowNotices from '../common/RowNotices';
import { PhotoButton } from '../common/PhotoGallery';
import { useSort, sortRows, SortTh } from '../common/SortHeader';
import ColumnFilter from '../common/ColumnFilter';
import { buildCableGroups, matchesGroupFilter, GroupFilterSelects, NO_GROUP_FILTER } from '../../utils/listGroups';
import './Cables.css';

const getCableVal = (cable, key) => {
  switch (key) {
    case 'tag':       return cable.tag || null;
    case 'from':      return endpointLabel(cable, 'from');
    case 'to':        return endpointLabel(cable, 'to');
    case 'cableType': return cable.cableType?.name || null;
    case 'section':   return cable.section?.number ?? null;
    case 'length_m':  return cable.length_m ?? null;
    case 'ordered':   return cable.ordered ? 1 : 0;
    case 'pulled':    return cable.pulled  ? 1 : 0;
    case 'note':      return cable.note || null;
    default:          return null;
  }
};

// Helpers for endpoint values: 'item:<id>' or 'brk:<id>'
const endpointVal = (itemObj, brkObj) => {
  if (brkObj?._id) return `brk:${brkObj._id}`;
  if (itemObj?._id) return `item:${itemObj._id}`;
  return '';
};

const decodeEndpoint = (ep = '') => {
  if (ep.startsWith('item:')) return { item: ep.slice(5), brk: undefined };
  if (ep.startsWith('brk:'))  return { item: undefined,  brk: ep.slice(4) };
  return { item: undefined, brk: undefined };
};

const itemLabel = (item) => {
  if (!item) return '—';
  return item.description || item.itemType || '(unnamed)';
};

const brkLabel = (brk) => {
  if (!brk) return '—';
  return `[${brk.switchboard?.name ?? 'SB'}] ${brk.tag}`;
};

const endpointLabel = (cable, end) => {
  if (end === 'from') return cable.fromBreaker ? brkLabel(cable.fromBreaker) : itemLabel(cable.from);
  return cable.toBreaker ? brkLabel(cable.toBreaker) : itemLabel(cable.to);
};

const cableToDraft = (c) => ({
  tag:          c.tag            || '',
  fromEndpoint: endpointVal(c.from, c.fromBreaker),
  toEndpoint:   endpointVal(c.to,   c.toBreaker),
  cableType:    c.cableType?._id  || '',
  section:      c.section?._id   || '',
  length_m:     c.length_m       ?? '',
  ordered:      c.ordered        ?? false,
  pulled:       c.pulled         ?? false,
  note:         c.note           || '',
  notices:      c.notices        || [],
});

const emptyDraft = () => ({
  tag: '', fromEndpoint: '', toEndpoint: '', cableType: '', section: '',
  length_m: '', ordered: false, pulled: false, note: '', notices: [],
});

const draftToPayload = (d, projectId) => {
  const from = decodeEndpoint(d.fromEndpoint);
  const to   = decodeEndpoint(d.toEndpoint);
  return {
    project:     projectId,
    tag:         d.tag       || undefined,
    from:        from.item   || null,
    fromBreaker: from.brk    || null,
    to:          to.item     || null,
    toBreaker:   to.brk      || null,
    cableType:   d.cableType || undefined,
    section:     d.section   || undefined,
    length_m:    d.length_m  !== '' ? parseFloat(d.length_m) : undefined,
    ordered:     d.ordered,
    pulled:      d.pulled,
    note:        d.note      || undefined,
    notices:     d.notices,
  };
};

const rowNav = (e, trEl, { save, cancel, up, down }) => {
  const cells   = [...trEl.querySelectorAll('td[data-cell]')];
  const curCell = cells.find(td => td.contains(document.activeElement));
  const idx     = cells.indexOf(curCell);
  const goTo    = (i) => { if (i < 0 || i >= cells.length) return false; cells[i].querySelector('.ss-trigger, input, select')?.focus(); return true; };

  if (e.key === 'ArrowUp')   { e.preventDefault(); up?.(idx); return; }
  if (e.key === 'ArrowDown') { e.preventDefault(); down?.(idx); return; }
  if (e.key === 'Enter')     { if (e.target.tagName === 'SELECT') return; e.preventDefault(); if (down) down(idx); else save(); return; }
  if (e.key === 'Tab') {
    e.preventDefault();
    if (e.shiftKey) { if (!goTo(idx - 1) && up) up(cells.length - 1); }
    else { if (!goTo(idx + 1)) { if (down) down(0); else save(); } }
    return;
  }
  if (e.key === 'F2') {
    e.preventDefault();
    const el = e.target;
    if (el.type === 'number') { const v = el.value; el.value = ''; el.value = v; }
    else { try { const len = el.value?.length ?? 0; el.setSelectionRange(len, len); } catch {} }
    return;
  }
  if (e.key === 'Escape') { e.preventDefault(); return; }
  if (e.key === 'ArrowRight') {
    const el = document.activeElement;
    if (el && el.tagName === 'INPUT' && el.type !== 'number' && el.selectionStart !== null && el.selectionEnd < el.value.length) return;
    e.preventDefault(); cells[idx + 1]?.querySelector('.ss-trigger, input, select')?.focus(); return;
  }
  if (e.key === 'ArrowLeft') {
    const el = document.activeElement;
    if (el && el.tagName === 'INPUT' && el.type !== 'number' && el.selectionStart !== null && el.selectionStart > 0) return;
    e.preventDefault(); cells[idx - 1]?.querySelector('.ss-trigger, input, select')?.focus(); return;
  }
};

const CABLE_FILTER_TYPE = {
  tag: 'text', from: 'select', to: 'select', cableType: 'select',
  section: 'select', length_m: 'text', ordered: 'select', pulled: 'select', note: 'text',
};
const CABLE_BOOL_COLS = new Set(['ordered', 'pulled']);

const getCableFilterText = (c, key) => {
  switch (key) {
    case 'tag':       return c.tag || '';
    case 'from':      return endpointLabel(c, 'from');
    case 'to':        return endpointLabel(c, 'to');
    case 'cableType': return c.cableType?.name || '';
    case 'section':   return c.section ? `${c.section.number} — ${c.section.text}` : '';
    case 'length_m':  return c.length_m != null ? String(c.length_m) : '';
    case 'ordered':   return c.ordered ? '1' : '0';
    case 'pulled':    return c.pulled ? '1' : '0';
    case 'note':      return c.note || '';
    default:          return '';
  }
};

function CableList({ project, isActive, role, highlightTag, onOpenTab }) {
  const [cables, setCables]         = useState([]);
  const [items, setItems]           = useState([]);
  const [cableTypes, setCableTypes] = useState([]);
  const [sections, setSections]     = useState([]);
  const [breakers, setBreakers]     = useState([]);
  const [editingId, setEditingId]   = useState(null);
  const [draft, setDraft]           = useState(null);
  const [saveErr, setSaveErr]       = useState('');
  const [newRow, setNewRow]         = useState(emptyDraft);
  const [newErr, setNewErr]         = useState('');
  const [search, setSearch]         = useState(() => sessionStorage.getItem(`cable-search-${project?._id}`) || '');
  const [sort, toggleSort]          = useSort('tag');
  const sortInitRef = useRef(false);
  const [copiedId, setCopiedId]     = useState(null);
  const [labelPrinter, setLabelPrinter] = useState(false);
  const [selected, setSelected]         = useState(new Set());
  const [labelPrinting, setLabelPrinting] = useState(false);
  const [labelMsg, setLabelMsg]           = useState('');
  const [filters, setFilters]             = useState({});
  const [groupFilter, setGroupFilter]     = useState(NO_GROUP_FILTER);
  const newRowRef         = useRef(null);
  const editRowRef        = useRef(null);
  const saveTimer         = useRef(null);
  const pendingFocusCol   = useRef(0);
  const undoRef      = useRef([]);
  const redoRef      = useRef([]);
  const beforeMapRef = useRef({});
  const undoRedoRef  = useRef(null);
  const saveSeqRef   = useRef({});
  const lastFocusRef   = useRef(null);
  const pendingRestore = useRef(false);
  const pendingEditRef = useRef(null);

  const canEdit  = ['admin','engineer','manager','foreman'].includes(role);
  const canPrint = ['admin','engineer','manager','foreman','team-leader'].includes(role);
  const sel = e => e.target.select();

  useEffect(() => {
    if (highlightTag) setSearch(highlightTag);
  }, [highlightTag]);

  useEffect(() => {
    if (project?._id) sessionStorage.setItem(`cable-search-${project._id}`, search);
  }, [search, project?._id]);

  const toggleSelect = (id) => setSelected(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const handlePrintLabels = async () => {
    const toPrint = cables.filter(c => selected.has(c._id));
    if (!toPrint.length) return;
    setLabelPrinting(true);
    setLabelMsg('');
    try {
      const labels = toPrint.map(c => ({
        pngBase64: getCableLabelDataUrl(c).split(',')[1],
        cableTag: c.tag || '',
      }));
      await submitLabelJob(labels);
      setLabelMsg(`Sent ${labels.length} label${labels.length > 1 ? 's' : ''} to printer`);
      setSelected(new Set());
    } catch (err) {
      setLabelMsg(err.response?.data?.msg || 'Print failed');
    } finally {
      setLabelPrinting(false);
      setTimeout(() => setLabelMsg(''), 5000);
    }
  };

  const setPhotos = (id) => (photos) =>
    setCables(prev => prev.map(c => c._id === id ? { ...c, photos } : c));

  const photoBtn = (cable) => (
    <PhotoButton kind="cables" record={cable} title={cable.tag || 'Cable'}
      role={role} onPhotosChange={setPhotos(cable._id)} />
  );

  const handleCopy = (cable) => {
    const nbNo = project?.newbuildingNo;
    const text = nbNo ? `%${nbNo}-${cable.tag}` : `%${cable.tag}`;
    navigator.clipboard.writeText(text).then(() => {
      setCopiedId(cable._id);
      setTimeout(() => setCopiedId(null), 1500);
    }).catch(() => {});
  };

  useEffect(() => {
    getLabelPrinter().then(r => setLabelPrinter(r.data.available)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!project || !isActive) return;
    Promise.all([
      getCables(project._id),
      getItems(project._id),
      getCableTypes(project._id),
      getSections(project._id),
      getBreakers(project._id),
    ]).then(([ca, it, ct, se, br]) => {
      setCables(sortRows(ca.data, sort, getCableVal));
      sortInitRef.current = true;
      setItems(it.data);
      setCableTypes(ct.data);
      setSections(se.data);
      setBreakers(br.data);
    }).catch(() => {});
  }, [project?._id, isActive]);

  useEffect(() => {
    if (!sortInitRef.current) return;
    setCables(prev => sortRows(prev, sort, getCableVal));
  }, [sort.key, sort.dir]);

  useEffect(() => {
    if (!isActive) return;
    const onKeyDown = async (e) => {
      if (!e.ctrlKey || (e.key !== 'z' && e.key !== 'y')) return;
      const el = document.activeElement;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA') && !el.closest('.module-wrap')) return;
      e.preventDefault();
      if (e.key === 'z') await undoRedoRef.current?.undo();
      else await undoRedoRef.current?.redo();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isActive]);

  const trackFocus = (e) => {
    const tr = e.target.closest('tr');
    if (!tr) return;
    const rowId = tr.dataset.rowId || (tr === newRowRef.current ? '_new' : editingId);
    if (!rowId) return;
    const cells = [...tr.querySelectorAll('td[data-cell]')];
    const colIdx = cells.findIndex(td => td.contains(e.target));
    lastFocusRef.current = { rowId, colIdx: Math.max(0, colIdx) };
  };

  useEffect(() => {
    if (isActive) pendingRestore.current = true;
  }, [isActive]);

  useLayoutEffect(() => {
    if (!canEdit) return;
    if (pendingRestore.current && sorted.length) {
      pendingRestore.current = false;
      const lf = lastFocusRef.current;
      if (editingId) {
        setTimeout(() => {
          const cells = [...(editRowRef.current?.querySelectorAll('td[data-cell]') || [])];
          const col = lf?.colIdx || 0;
          (cells[col] || cells[0])?.querySelector('.ss-trigger, input, select')?.focus();
        }, 0);
        return;
      }
      if (lf && lf.rowId === '_new') {
        setTimeout(() => {
          const cells = [...(newRowRef.current?.querySelectorAll('td[data-cell]') || [])];
          (cells[lf.colIdx] || cells[0])?.querySelector('.ss-trigger, input, select')?.focus();
        }, 0);
        return;
      }
      const row = lf ? sorted.find(c => c._id === lf.rowId) : null;
      startEdit(row || sorted[0], lf?.colIdx || 0);
      return;
    }
    if (pendingEditRef.current && !editingId && sorted.length) {
      const { id, colIdx } = pendingEditRef.current;
      pendingEditRef.current = null;
      const row = sorted.find(c => c._id === id);
      if (row) startEdit(row, colIdx);
      else if (sorted.length) startEdit(sorted[0], 0);
    }
  });

  const autoSave = (d, id) => {
    if (!id || id === '_new' || !project) return;
    clearTimeout(saveTimer.current);
    const seq = (saveSeqRef.current[id] = (saveSeqRef.current[id] || 0) + 1);
    saveTimer.current = setTimeout(async () => {
      try {
        const res = await updateCable(id, draftToPayload(d, project._id));
        if (saveSeqRef.current[id] !== seq) return;
        setCables(prev => prev.map(c => c._id === id ? res.data : c));
      } catch (e) { setSaveErr(e.response?.data?.msg || 'Save failed.'); }
    }, 500);
  };

  const pushHistory = (id, before, after) => {
    if (!before || JSON.stringify(before) === JSON.stringify(after)) return;
    undoRef.current = [{ id, before, after }, ...undoRef.current].slice(0, 30);
    redoRef.current = [];
  };

  const flushSave = async (d, id) => {
    clearTimeout(saveTimer.current);
    if (!d || !id || id === '_new' || !project) return;
    const seq = (saveSeqRef.current[id] = (saveSeqRef.current[id] || 0) + 1);
    try {
      const res = await updateCable(id, draftToPayload(d, project._id));
      if (saveSeqRef.current[id] !== seq) return;
      setCables(prev => prev.map(c => c._id === id ? res.data : c));
    } catch (e) { setSaveErr(e.response?.data?.msg || 'Save failed.'); }
  };

  const setField    = (k) => (v) => setDraft(prev => { const n = { ...prev, [k]: v }; autoSave(n, editingId); return n; });
  const setNewField = (k) => (v) => setNewRow(d => ({ ...d, [k]: v }));

  // Focus the correct column whenever the edit row changes
  useLayoutEffect(() => {
    if (!editingId) return;
    const focusCol = () => {
      if (!editRowRef.current) return false;
      const cells = [...editRowRef.current.querySelectorAll('td[data-cell]')];
      const el = (cells[pendingFocusCol.current] ?? cells[0])?.querySelector('.ss-trigger, input, select');
      if (el) { el.focus(); return true; }
      return false;
    };
    if (!focusCol()) setTimeout(focusCol, 0);
  }, [editingId]);

  const startEdit = (cable, colIdx = 0) => {
    if (!canEdit) return;
    clearTimeout(saveTimer.current);
    if (editingId && draft) {
      pushHistory(editingId, beforeMapRef.current[editingId], draft);
      delete beforeMapRef.current[editingId];
      flushSave(draft, editingId);
    }
    beforeMapRef.current[cable._id] = cableToDraft(cable);
    pendingFocusCol.current = colIdx;
    setEditingId(cable._id); setDraft(cableToDraft(cable)); setSaveErr('');
  };

  const cancelEdit = (cableId) => {
    clearTimeout(saveTimer.current);
    delete beforeMapRef.current[cableId];
    setEditingId(null); setDraft(null); setSaveErr('');
    if (cableId) setTimeout(() => document.querySelector(`tr[data-row-id="${cableId}"]`)?.focus(), 0);
  };

  const commitEdit = async () => {
    clearTimeout(saveTimer.current);
    const id = editingId;
    if (draft && id) {
      pushHistory(id, beforeMapRef.current[id], draft);
      delete beforeMapRef.current[id];
      await flushSave(draft, id);
    }
    setEditingId(null); setDraft(null);
    if (id) setTimeout(() => document.querySelector(`tr[data-row-id="${id}"]`)?.focus(), 0);
  };

  const saveNew = async () => {
    const hasData = Object.entries(newRow).some(([k, v]) =>
      typeof v !== 'boolean' && !Array.isArray(v) && v !== '' && v != null);
    if (!hasData) return;
    setNewErr('');
    try {
      const res = await createCable(draftToPayload(newRow, project._id));
      setCables(prev => [...prev, res.data]);
      setNewRow(emptyDraft());
      setTimeout(() => newRowRef.current?.querySelector('.ss-trigger, input, select')?.focus(), 0);
    } catch (e) { setNewErr(e.response?.data?.msg || 'Save failed.'); }
  };

  const removeCable = async (id) => {
    if (!confirm('Delete this cable?')) return;
    clearTimeout(saveTimer.current);
    const idx = sorted.findIndex(c => c._id === id);
    const neighbor = sorted[idx + 1] || sorted[idx - 1];
    try {
      await deleteCable(id);
      setCables(prev => prev.filter(c => c._id !== id));
      if (editingId === id) { setEditingId(null); setDraft(null); setSaveErr(''); }
      if (neighbor) pendingEditRef.current = { id: neighbor._id, colIdx: 0 };
    } catch { setSaveErr('Delete failed.'); }
  };

  const toggleOrdered = async (cable, e) => {
    e.stopPropagation(); if (!canEdit) return;
    try {
      const res = await updateCable(cable._id, { ordered: !cable.ordered });
      setCables(prev => prev.map(c => c._id === cable._id ? res.data : c));
    } catch {}
  };

  const togglePulled = async (cable, e) => {
    e.stopPropagation(); if (!canEdit) return;
    try {
      const res = await updateCable(cable._id, { pulled: !cable.pulled });
      setCables(prev => prev.map(c => c._id === cable._id ? res.data : c));
    } catch {}
  };

  const handleNavRow = (dir, currentId, colIdx = 0) => {
    clearTimeout(saveTimer.current);
    const isNew = currentId === '_new';
    if (!isNew && draft) {
      pushHistory(currentId, beforeMapRef.current[currentId], draft);
      delete beforeMapRef.current[currentId];
      flushSave(draft, currentId);
    } else if (isNew) saveNew();

    if (isNew) {
      if (dir === 'up' && sorted.length > 0) {
        const t = sorted[sorted.length - 1];
        beforeMapRef.current[t._id] = cableToDraft(t);
        pendingFocusCol.current = colIdx;
        setEditingId(t._id); setDraft(cableToDraft(t));
      }
      return;
    }
    const idx = sorted.findIndex(c => c._id === currentId);
    if (dir === 'up') {
      if (idx > 0) {
        beforeMapRef.current[sorted[idx-1]._id] = cableToDraft(sorted[idx-1]);
        pendingFocusCol.current = colIdx;
        setEditingId(sorted[idx-1]._id); setDraft(cableToDraft(sorted[idx-1]));
      }
      // else: already at top — stay
    } else {
      if (idx < sorted.length - 1) {
        beforeMapRef.current[sorted[idx+1]._id] = cableToDraft(sorted[idx+1]);
        pendingFocusCol.current = colIdx;
        setEditingId(sorted[idx+1]._id); setDraft(cableToDraft(sorted[idx+1]));
      } else {
        setEditingId(null); setDraft(null);
        setTimeout(() => {
          const cells = [...(newRowRef.current?.querySelectorAll('td[data-cell]') || [])];
          ((cells[colIdx] ?? cells[0])?.querySelector('.ss-trigger, input, select'))?.focus();
        }, 0);
      }
    }
  };

  // Combined options: items first, then breakers (prefixed so we know which type)
  const endpointOpts = [
    ...items.map(i => ({ value: `item:${i._id}`, label: itemLabel(i) })),
    ...breakers.map(b => ({ value: `brk:${b._id}`, label: brkLabel(b) })),
  ];
  const ctOpts      = cableTypes.map(t => ({ value: t._id, label: t.name }));
  const sectionOpts = sections.map(s => ({ value: s._id, label: `${s.number} — ${s.text}` }));

  const filterOpts = useMemo(() => {
    const opts = {};
    for (const [key, type] of Object.entries(CABLE_FILTER_TYPE)) {
      if (type !== 'select') continue;
      const vals = new Set();
      cables.forEach(c => vals.add(getCableFilterText(c, key)));
      const isBool = CABLE_BOOL_COLS.has(key);
      opts[key] = [...vals].sort().map(v => ({
        value: v,
        label: isBool ? (v === '1' ? 'Yes' : 'No') : (v || '(Empty)'),
      }));
    }
    return opts;
  }, [cables]);

  const setFilter = (key, val) => setFilters(prev => {
    const next = { ...prev };
    if (val === undefined || val === '' || val === null) delete next[key];
    else next[key] = val;
    return next;
  });

  const cf = (key) => {
    const type = CABLE_FILTER_TYPE[key];
    if (!type) return undefined;
    return <ColumnFilter type={type} value={filters[key]} options={type === 'select' ? filterOpts[key] : undefined} onChange={v => setFilter(key, v)} />;
  };

  // Which systems / switchboards / rooms each cable touches (From or To end), plus the dropdown options
  const { groups: cableGroups, options: groupOpts } = useMemo(
    () => buildCableGroups(cables, items, breakers), [cables, items, breakers]);

  const hasGroupFilter = Object.values(groupFilter).some(Boolean);
  const hasFilters = Object.keys(filters).length > 0 || hasGroupFilter;
  const clearFilters = () => { setFilters({}); setGroupFilter(NO_GROUP_FILTER); };

  const sorted = cables.filter(c => {
    if (search) {
      const q = search.toLowerCase();
      if (!((c.tag || '').toLowerCase().includes(q) ||
            endpointLabel(c, 'from').toLowerCase().includes(q) ||
            endpointLabel(c, 'to').toLowerCase().includes(q) ||
            (c.cableType?.name || '').toLowerCase().includes(q) ||
            (c.section ? `${c.section.number} ${c.section.text}` : '').toLowerCase().includes(q))) return false;
    }
    if (hasGroupFilter && !matchesGroupFilter(cableGroups, c._id, groupFilter)) return false;
    for (const [key, filter] of Object.entries(filters)) {
      const val = getCableFilterText(c, key);
      if (typeof filter === 'string') {
        if (!val.toLowerCase().includes(filter.toLowerCase())) return false;
      } else if (filter instanceof Set) {
        if (!filter.has(val)) return false;
      }
    }
    return true;
  });

  undoRedoRef.current = {
    undo: async () => {
      if (!undoRef.current.length) return;
      const [entry, ...rest] = undoRef.current;
      undoRef.current = rest;
      redoRef.current = [entry, ...redoRef.current].slice(0, 30);
      if (editingId === entry.id) { setEditingId(null); setDraft(null); }
      await flushSave(entry.before, entry.id);
      pendingEditRef.current = { id: entry.id, colIdx: 0 };
    },
    redo: async () => {
      if (!redoRef.current.length) return;
      const [entry, ...rest] = redoRef.current;
      redoRef.current = rest;
      undoRef.current = [entry, ...undoRef.current].slice(0, 30);
      if (editingId === entry.id) { setEditingId(null); setDraft(null); }
      await flushSave(entry.after, entry.id);
      pendingEditRef.current = { id: entry.id, colIdx: 0 };
    },
  };

  if (!project) return <div className="empty-state">Select a project first.</div>;

  const renderEditCells = (d, setF) => (
    <>
      <td data-cell><input value={d.tag} onChange={e => setF('tag')(e.target.value)} onFocus={sel} placeholder="Tag" className="cell-input-mono" /></td>
      <td data-cell><SearchSelect options={endpointOpts} value={d.fromEndpoint} onChange={setF('fromEndpoint')} /></td>
      <td data-cell><SearchSelect options={endpointOpts} value={d.toEndpoint}   onChange={setF('toEndpoint')} /></td>
      <td data-cell><SearchSelect options={ctOpts}       value={d.cableType}    onChange={setF('cableType')} /></td>
      <td data-cell><SearchSelect options={sectionOpts}  value={d.section}      onChange={setF('section')} /></td>
      <td data-cell><input type="number" value={d.length_m} onChange={e => setF('length_m')(e.target.value)} onFocus={sel} className="num-input" /></td>
      <td data-cell className="cell-check"><input type="checkbox" checked={d.ordered} onChange={e => setF('ordered')(e.target.checked)} /></td>
      <td data-cell className="cell-check"><input type="checkbox" checked={d.pulled}  onChange={e => setF('pulled')(e.target.checked)} /></td>
      <td data-cell><input value={d.note} onChange={e => setF('note')(e.target.value)} onFocus={sel} placeholder="Note" className="cell-input-wide" /></td>
      <td data-cell style={{ position: 'relative' }}>
        <RowNotices notices={d.notices} editing onChange={v => setF('notices')(v)} />
      </td>
    </>
  );

  return (
    <div className="module-wrap">
      <div className="module-toolbar cable-toolbar">
        <span className="module-toolbar-title">Cable Register</span>
        <input className="module-search" placeholder="Search…" value={search} onChange={e => setSearch(e.target.value)} />
        <GroupFilterSelects options={groupOpts} value={groupFilter} onChange={setGroupFilter} />
        <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>{sorted.length} cables</span>
        {hasFilters && <button className="btn-link" style={{ fontSize: 11 }} onClick={clearFilters}>Clear filters</button>}
        {labelPrinter && selected.size > 0 && (
          <button className="btn-secondary" style={{ fontSize: 12, padding: '3px 10px' }}
            disabled={labelPrinting} onClick={handlePrintLabels}>
            <MdPrint size={13} style={{ marginRight: 4 }} />
            {labelPrinting ? 'Sending…' : `Print Labels (${selected.size})`}
          </button>
        )}
        {labelMsg && <span style={{ fontSize: 11, color: labelMsg.startsWith('Sent') ? '#4ec994' : '#f48771' }}>{labelMsg}</span>}
        {canPrint && <PrintButton
          title={`Cable Register — ${project?.name ?? ''}`}
          storageKey="cables"
          rows={sorted}
          onOpenTab={onOpenTab}
          columns={[
            { key: 'tag',       label: 'Tag',        render: c => c.tag || '' },
            { key: 'from',      label: 'From',       render: c => endpointLabel(c, 'from') },
            { key: 'to',        label: 'To',         render: c => endpointLabel(c, 'to') },
            { key: 'cableType', label: 'Type',       render: c => c.cableType?.name || '' },
            { key: 'section',   label: 'Section',    render: c => c.section ? `${c.section.number} — ${c.section.text}` : '' },
            { key: 'length_m',  label: 'Length (m)', render: c => c.length_m ?? '' },
            { key: 'ordered',   label: 'Ordered',    render: c => c.ordered ? '✓' : '' },
            { key: 'pulled',    label: 'Pulled',     render: c => c.pulled  ? '✓' : '' },
            { key: 'note',      label: 'Note',       render: c => c.note || '', defaultOn: false },
          ]}
        />}
      </div>
      {(saveErr || newErr) && <div className="row-error">{saveErr || newErr}</div>}
      <div className="module-body">
        <table className="data-table cable-table" style={{ tableLayout: 'fixed' }} onFocusCapture={trackFocus}>
          <colgroup>
            {labelPrinter && <col style={{ width: 32 }} />}{/* Select */}
            <col style={{ width: 100 }} />{/* Tag */}
            <col style={{ width: 200 }} />{/* From */}
            <col style={{ width: 200 }} />{/* To */}
            <col style={{ width: 110 }} />{/* Type */}
            <col style={{ width: 120 }} />{/* Section */}
            <col style={{ width: 75 }}  />{/* Length */}
            <col style={{ width: 42 }}  />{/* Ordered */}
            <col style={{ width: 42 }}  />{/* Pulled */}
            <col style={{ width: 150 }} />{/* Note */}
            <col style={{ width: 70 }}  />{/* Notices */}
            <col style={{ width: 56 }} />{/* Photos + Copy */}
            {canEdit && <col style={{ width: 36 }} />}
          </colgroup>
          <thead>
            <tr>
              {labelPrinter && (
                <th className="cell-check" style={{ width: 32 }}>
                  <input type="checkbox"
                    checked={sorted.length > 0 && sorted.every(c => selected.has(c._id))}
                    onChange={() => {
                      if (sorted.every(c => selected.has(c._id))) setSelected(new Set());
                      else setSelected(new Set(sorted.map(c => c._id)));
                    }} />
                </th>
              )}
              <SortTh sortKey="tag"       sort={sort} onSort={toggleSort} filter={cf('tag')}>Tag</SortTh>
              <SortTh sortKey="from"      sort={sort} onSort={toggleSort} filter={cf('from')}>From</SortTh>
              <SortTh sortKey="to"        sort={sort} onSort={toggleSort} filter={cf('to')}>To</SortTh>
              <SortTh sortKey="cableType" sort={sort} onSort={toggleSort} filter={cf('cableType')}>Type</SortTh>
              <SortTh sortKey="section"   sort={sort} onSort={toggleSort} filter={cf('section')}>Section</SortTh>
              <SortTh sortKey="length_m"  sort={sort} onSort={toggleSort} filter={cf('length_m')}>Length (m)</SortTh>
              <SortTh sortKey="ordered"   sort={sort} onSort={toggleSort} filter={cf('ordered')} title="Ordered / Cut">Ord.</SortTh>
              <SortTh sortKey="pulled"    sort={sort} onSort={toggleSort} filter={cf('pulled')} title="Pulled">Pulled</SortTh>
              <SortTh sortKey="note"      sort={sort} onSort={toggleSort} filter={cf('note')}>Note</SortTh>
              <th>Notices</th>
              <th style={{ width: 56 }}></th>
              {canEdit && <th style={{ width: 32 }}></th>}
            </tr>
          </thead>
          <tbody>
            {sorted.map(cable => {
              if (editingId === cable._id) {
                return (
                  <tr key={cable._id} ref={editRowRef} className="row-editing"
                    onKeyDown={e => {
                      if (e.key === 'F2') {
                        e.preventDefault();
                        const el = e.target;
                        if (el.type === 'number') { const v = el.value; el.value = ''; el.value = v; }
                        else { try { const len = el.value?.length ?? 0; el.setSelectionRange(len, len); } catch {} }
                        return;
                      }
                      rowNav(e, e.currentTarget, {
                        save:   commitEdit,
                        cancel: () => cancelEdit(cable._id),
                        up:     (ci) => handleNavRow('up',   cable._id, ci),
                        down:   (ci) => handleNavRow('down', cable._id, ci),
                      });
                    }}>
                    {labelPrinter && (
                      <td className="cell-check" onClick={e => e.stopPropagation()}>
                        <input type="checkbox" checked={selected.has(cable._id)}
                          onChange={() => toggleSelect(cable._id)} />
                      </td>
                    )}
                    {renderEditCells(draft, setField)}
                    <td className="cell-actions" onClick={e => e.stopPropagation()}>{photoBtn(cable)}</td>
                    <td className="cell-actions" onClick={e => e.stopPropagation()}>
                      <button className="btn-icon btn-icon-danger" title="Delete" onClick={() => removeCable(cable._id)}><VscTrash size={13} /></button>
                    </td>
                  </tr>
                );
              }
              return (
                <tr key={cable._id}
                  data-row-id={cable._id}
                  tabIndex={canEdit ? -1 : undefined}
                  className={canEdit ? 'data-row-click' : ''}
                  onClick={e => {
                    const cells = [...e.currentTarget.querySelectorAll('td[data-cell]')];
                    const clicked = cells.findIndex(td => td.contains(e.target));
                    startEdit(cable, Math.max(0, clicked));
                  }}
                  onKeyDown={!canEdit ? undefined : e => {
                    if (e.key === 'Escape') { e.preventDefault(); return; }
                    e.preventDefault();
                    if (e.key === 'ArrowDown') {
                      const idx = sorted.findIndex(c => c._id === cable._id);
                      if (idx < sorted.length - 1) startEdit(sorted[idx + 1], 0);
                      else newRowRef.current?.querySelector('.ss-trigger, input, select')?.focus();
                    } else if (e.key === 'ArrowUp') {
                      const idx = sorted.findIndex(c => c._id === cable._id);
                      if (idx > 0) startEdit(sorted[idx - 1], 0);
                    } else if (!e.ctrlKey && !e.metaKey && !e.altKey) {
                      startEdit(cable, 0);
                    }
                  }}>
                  {labelPrinter && (
                    <td className="cell-check" onClick={e => e.stopPropagation()}>
                      <input type="checkbox" checked={selected.has(cable._id)}
                        onChange={() => toggleSelect(cable._id)} />
                    </td>
                  )}
                  <td style={{ fontFamily: 'monospace' }}>{cable.tag || '—'}</td>
                  <td className="cell-from-to">{endpointLabel(cable, 'from')}</td>
                  <td className="cell-from-to">{endpointLabel(cable, 'to')}</td>
                  <td>{cable.cableType?.name || '—'}</td>
                  <td>{cable.section ? `${cable.section.number} — ${cable.section.text}` : '—'}</td>
                  <td className="cell-num">{cable.length_m ?? '—'}</td>
                  <td className="cell-check" onClick={e => toggleOrdered(cable, e)}><input type="checkbox" checked={!!cable.ordered} readOnly /></td>
                  <td className="cell-check" onClick={e => togglePulled(cable, e)}><input type="checkbox" checked={!!cable.pulled} readOnly /></td>
                  <td className="cell-note">{cable.note || '—'}</td>
                  <td><RowNotices notices={cable.notices || []} /></td>
                  <td className="cell-actions" onClick={e => e.stopPropagation()}>
                    {photoBtn(cable)}
                    {role !== 'client' && (
                      <button className="btn-icon" title="Copy chat link" onClick={() => handleCopy(cable)}>
                        {copiedId === cable._id ? <VscCheck size={12} /> : <VscCopy size={12} />}
                      </button>
                    )}
                  </td>
                  {canEdit && <td />}
                </tr>
              );
            })}
            {canEdit && (
              <tr ref={newRowRef} className="row-editing row-new"
                onKeyDown={e => rowNav(e, e.currentTarget, {
                  save:   saveNew,
                  cancel: () => setNewRow(emptyDraft()),
                  up:     () => handleNavRow('up', '_new'),
                  down:   null,
                })}>
                {labelPrinter && <td />}
                {renderEditCells(newRow, setNewField)}
                <td /><td />
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default CableList;
